import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'gangtok', name: 'Gangtok', homestays: 520, valleys: ['MG Marg', 'Rumtek', 'Tsomgo Lake', 'Nathula'], altitude: '1,650–4,300m', highlight: true, division: 'East' },
  { id: 'gyalshing', name: 'Gyalshing', homestays: 380, valleys: ['Pelling', 'Rinchenpong', 'Pemayangtse', 'Khecheopalri'], altitude: '1,500–5,500m', highlight: true, division: 'West' },
  { id: 'mangan', name: 'Mangan', homestays: 320, valleys: ['Lachen', 'Lachung', 'Gurudongmar', 'Yumthang'], altitude: '1,500–5,400m', highlight: true, division: 'North' },
  { id: 'namchi', name: 'Namchi', homestays: 280, valleys: ['Ravangla', 'Temi Tea Garden', 'Tendong Hill'], altitude: '1,300–2,600m', highlight: true, division: 'South' },
  { id: 'pakyong', name: 'Pakyong', homestays: 220, valleys: ['Zuluk', 'Nathang Valley', 'Thambi View Point'], altitude: '1,300–4,500m', highlight: true, division: 'East' },
  { id: 'soreng', name: 'Soreng', homestays: 180, valleys: ['Okhrey', 'Hilley', 'Barsey Rhododendron', 'Uttarey'], altitude: '1,600–4,000m', highlight: true, division: 'West' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Sikkim Tourism Department, digitized and made discoverable. Basic listing with location, contact, and license verification.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on district map', 'Basic contact listing', 'Searchable by valley & altitude'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified photos, organic kitchens, and traveller-ready listings in India\'s cleanest state.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The treasures of Sikkimese hospitality.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay in Sikkim — from Gangtok\'s ridge to Lachen\'s yak trails — searchable, verified, bookable on one platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, rhododendron season patterns, and district-level occupancy insights for sustainable tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy for hosts — empowering Lepcha, Bhutia, and Nepali families as micro-entrepreneurs.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification maintaining standards while preserving Sikkim\'s organic ethos and the warmth of mountain hospitality.' },
];

function SikkimMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'mangan': { d: 'M100 20 L220 15 L260 60 L250 130 L200 160 L140 140 L90 100 L80 50 Z', cx: 170, cy: 85 },
    'gangtok': { d: 'M200 160 L250 130 L280 170 L270 220 L230 240 L190 220 L180 180 Z', cx: 230, cy: 190 },
    'pakyong': { d: 'M230 240 L270 220 L300 250 L290 300 L250 310 L220 280 Z', cx: 262, cy: 270 },
    'gyalshing': { d: 'M30 130 L90 100 L140 140 L150 200 L120 240 L70 240 L30 200 Z', cx: 92, cy: 178 },
    'soreng': { d: 'M30 200 L70 240 L90 290 L60 310 L20 290 L10 250 Z', cx: 48, cy: 265 },
    'namchi': { d: 'M120 240 L150 200 L200 160 L190 220 L230 240 L220 280 L180 310 L130 300 L90 290 L70 240 Z', cx: 155, cy: 265 },
  };

  return (
    <svg viewBox="0 0 320 340" className="w-full max-w-md mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path
              d={path.d}
              fill={isActive ? '#2D5A27' : isHighlight ? 'rgba(45,90,39,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#7BC96F' : 'rgba(255,255,255,0.3)'}
              strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }}
            />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 12 : 10}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 16} textAnchor="middle" dominantBaseline="middle"
                fill="#7BC96F" fontSize={9} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function SikkimStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="sikkim-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="sikkim" />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1718696070605-db38369c5629?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Sikkim &middot; The Organic State</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 6 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Small enough to know by heart,<br />vast enough to lose yourself forever
          </h2>
        </div>
      </section>

      {/* Story */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#7BC96F] text-[10px] tracking-[0.5em] uppercase mb-6">Denzong &middot; Valley of Rice</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            In the shadow of the world's third highest peak,<br />
            the smallest state keeps the biggest heart.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            Sikkim is where Kangchenjunga guards the northern horizon like a deity — because to 
            the Lepcha, Bhutia, and Nepali families who live here, it is one. This is India's first 
            fully organic state, where cardamom dries on rooftops, where rhododendrons paint entire 
            mountainsides crimson in April, and where a grandmother in Ravangla will serve you 
            homemade tongba while prayer wheels spin in the monastery next door. Every homestay 
            here grows its own food, composts its own waste, and wakes to a sunrise that paints 
            the five peaks of Kangchenjunga in shades the rest of the world only sees in dreams. 
            When you stay here, you don't visit Sikkim — you become part of its quiet, green, 
            deeply generous way of being.
          </p>
        </div>
      </section>

      {/* Living Map */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#7BC96F] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Six districts. Four directions. One guardian peak.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <SikkimMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#7BC96F] text-[10px] tracking-[0.4em] uppercase">District</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Sikkim</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a district to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Sikkim</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#2D5A27] to-[#7BC96F] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Funnel */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#7BC96F] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise through the ranks — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#7BC96F] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner for state tourism departments — bringing every licensed homestay online, making them discoverable to millions of travellers, and providing real-time demand intelligence for tourism planning.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#7BC96F] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#7BC96F]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(123,201,111,0.05), transparent)' }}>
            <p className="text-[#7BC96F] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#7BC96F]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#7BC96F] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Sikkim shares its licensed homestay registry — GoHimalayan digitizes and maps every property</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#7BC96F]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#7BC96F] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into organic mountain communities</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#7BC96F]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#7BC96F] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, seasonal analytics, and sustainable tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Sikkim."
              className="inline-flex items-center gap-3 bg-[#7BC96F] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#8fd485] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#7BC96F" />

      <style>{`
        @keyframes expandBar {
          from { width: 0; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
